import {
  collection,
  collectionGroup,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  endAt,
  endBefore,
  FirestoreError,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Query,
  limit as queryLimit,
  startAfter,
  startAt,
  Timestamp,
  where,
  WhereFilterOp,
} from 'firebase/firestore';

import { firestore } from '../';

export interface FilterItem<Schema> {
  operator: WhereFilterOp;
  value: Date | string | number | Timestamp;
  attribute: keyof Schema & string;
}

export interface OrderItem<Schema> {
  type: 'asc' | 'desc';
  field: keyof Schema & string;
}

type ConstraintType = 'startAt' | 'startAfter' | 'endAt' | 'endBefore';

export type GetQueryProps<Schema> = {
  collectionPath: string;
  filters?: FilterItem<Schema>[];
  ordering?: OrderItem<Schema>[];
  snapshotConstraint?:
    | {
        type: ConstraintType;
        documentSnapshot: DocumentSnapshot;
      }
    | {
        type: ConstraintType;
        value: any;
      };
  limit?: number;
  type?: 'collection' | 'collectionGroup';
};

/**
 * Abstraction for generating firestore query using simplified definition.
 *
 * @example
 *
 * import { PuzzleSchema } from 'src/models/Puzzle';
 *
 * const someSnapshotFromPreviousQueries = previousQuery.docs[2];
 *
 * const artistPuzzlesQuery = getQuery<PuzzleSchema>({
 * 	// Can be also be subcollection like usersWeb/omcYThwUANRFoeCcBRKJRQf6LXG3/insights:
 * 	'puzzles',
 * 	// Only get puzzles from this artist:
 * 	[{attribute: 'Publisher', operator: '==', value: 'omcYThwUANRFoeCcBRKJRQf6LXG3'}],
 * 	// Sort puzzles by their creation in descending fashion.
 * 	[{type: 'desc', field: 'createdAt' }],
 * 	// Query returns documents only after specified one.
 * 	// This snapshot can come from separate query like getDoc that uses the same collection.
 * 	{type: 'startAfter', documentSnapshot: someSnapshotFromPreviousQueries},
 * 	// Using snapshot constraint - note value
 * 	snapshotConstraint: { type: 'startAfter', value: 1 },
 * });
 *
 * @param collectionPath
 * @param filters
 * @param ordering
 * @param snapshotConstraint
 * @param limit
 */
export const getQuery = <Schema>({
  collectionPath,
  filters = [],
  ordering = [],
  snapshotConstraint,
  limit,
  type = 'collection',
}: GetQueryProps<Schema>) => {
  const collectionRef =
    type === 'collection'
      ? collection(firestore, collectionPath)
      : collectionGroup(firestore, collectionPath);

  const queryRef = query(
    collectionRef,
    ...filters.map((filter) => where(filter?.attribute, filter?.operator, filter?.value)),
    ...ordering.map((order) => orderBy(order.field, order.type)),
    ...(snapshotConstraint
      ? [
          getSnapshotConstraint(
            snapshotConstraint.type,
            'documentSnapshot' in snapshotConstraint
              ? snapshotConstraint.documentSnapshot
              : snapshotConstraint.value,
          ),
        ]
      : []),
    ...(limit ? [queryLimit(limit)] : []),
  );

  return queryRef as Query<Schema>;
};

export const getSnapshotConstraint = (type: ConstraintType, documentSnapshot: DocumentSnapshot) => {
  switch (type) {
    case 'startAt':
      return startAt(documentSnapshot);
    case 'startAfter':
      return startAfter(documentSnapshot);
    case 'endAt':
      return endAt(documentSnapshot);
    case 'endBefore':
      return endBefore(documentSnapshot);
  }
};

export const doesUserExist = async (username: string) => {
  const usersQuery = getQuery<{ username: string }>({
    collectionPath: 'usersWeb',
    filters: [{ attribute: 'username', operator: '==', value: username }],
    limit: 1,
  });

  const usersSnapshot = await getDocs(usersQuery);
  return usersSnapshot.empty;
};

/**
 * Single fetch of document without subscribing to changes.
 *
 * @param path POSIX style document path.
 */
export const getDocument = (path: string) => {
  const documentReference = getDocumentReference(path);
  return getDoc(documentReference);
};

// Helper types for subscribeToDocument.
type OnSnapshot<T = DocumentData> = (snapshot: DocumentSnapshot<T>) => void;
type OnError = (error: FirestoreError) => void;

/**
 * Get document data and subscribe to changes.
 *
 * @example
 * // You can add your own expected response type instead of DocumentData and
 * // it will be automatically *asserted*.
 * const [data, setData] = useState<DocumentData | null>(null);
 * const [error, setError] = useState<FirestoreError | null>(null);
 *
 * subscribeToDocument('usersWeb/${props.artistId}', (snapshot) => {
 *    setData(snapshot.data())
 * }, (error) => {
 *    setError(error);
 * });
 *
 * @param path POSIX style document path.
 * @param onSnapshotHandler Updated data handler function.
 * @param onErrorHandler Error handler.
 */
export const subscribeToDocument = <T = DocumentData>(
  path: string,
  onSnapshotHandler: OnSnapshot<T>,
  onErrorHandler: OnError,
) => {
  const documentReference = getDocumentReference<T>(path);
  return onSnapshot(
    documentReference,
    (snapshot: DocumentSnapshot<T>) => onSnapshotHandler(snapshot),
    (error) => onErrorHandler(error),
  );
};

/**
 * Convenience function for getting document reference from given path,
 * similar to how it works on admin sdk.
 *
 * @param path POSIX style document path.
 */
export const getDocumentReference = <T = DocumentData>(path: string) => {
  const documentId = extractDocumentIdFromPath(path);
  const collectionPath = path.replace(`/${documentId}`, '');
  const collectionReference = collection(firestore, collectionPath);

  return doc(collectionReference, documentId) as DocumentReference<T>;
};

/**
 * Convenience function for getting collection reference from given path,
 * similar to how it works on admin sdk.
 * Make sure path has uneven number of segments which means that you are requesting collection reference,
 * as even number of segments indicates document.
 *
 * @param path POSIX style path to collection/subcollection.
 */
export const getCollectionReference = (collectionPath: string) => {
  return collection(firestore, collectionPath);
};

/**
 * Extract id of a firestore document by selecting last string chain after forward slash
 *
 * @param path POSIX style document path.
 */
function extractDocumentIdFromPath(path: string) {
  // Expected output example for valid path: Array [ "/L2qxwgCgVWJr01DL2eak", "L2qxwgCgVWJr01DL2eak" ]
  const searchResults = path.match(/\/(\w+)$/);

  if (!searchResults) throw new Error('error/malformed-path');

  return searchResults[1];
}
